import * as React from 'react';
import { useTranslation } from 'react-i18next';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { LayoutGrid, List } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Icon } from '@/components/atoms/Icon';
import styles from './ViewToggle.module.scss';
import type { ViewToggleProps } from './ViewToggle.types';

/**
 * ViewToggle - Segment buttons for switching between card and list view
 * Based on Radix UI Toggle Group (single mode, icon-only)
 *
 * @example
 * <ViewToggle value={viewMode} onValueChange={setViewMode} aria-label="View mode" />
 */
export function ViewToggle({
  value,
  onValueChange,
  defaultValue = 'card',
  disabled = false,
  className,
  'aria-label': ariaLabel,
  ...props
}: ViewToggleProps) {
  const { t } = useTranslation();
  const groupAria = ariaLabel ?? t('viewToggle.groupAria');
  const handleValueChange = React.useCallback(
    (newValue: string) => {
      if (newValue === 'card' || newValue === 'list') {
        onValueChange?.(newValue);
      }
    },
    [onValueChange]
  );

  return (
    <ToggleGroupPrimitive.Root
      type="single"
      value={value}
      onValueChange={handleValueChange}
      defaultValue={defaultValue}
      disabled={disabled}
      className={cn(styles.viewToggle, className)}
      aria-label={groupAria}
      {...props}
    >
      <ToggleGroupPrimitive.Item
        value="card"
        className={styles.viewToggleItem}
        aria-label={t('viewToggle.cardAria')}
      >
        <Icon icon={LayoutGrid} size="sm" aria-hidden />
      </ToggleGroupPrimitive.Item>
      <ToggleGroupPrimitive.Item
        value="list"
        className={styles.viewToggleItem}
        aria-label={t('viewToggle.listAria')}
      >
        <Icon icon={List} size="sm" aria-hidden />
      </ToggleGroupPrimitive.Item>
    </ToggleGroupPrimitive.Root>
  );
}

ViewToggle.displayName = 'ViewToggle';
